import { createContext, useCallback, useContext, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Lingua, TextoLocalizado } from './documento-do-site';

/**
 * Os textos das páginas da loja que o lojista trocou no construtor
 * (`PaginaDoSite.textos`, só as chaves de `TEXTOS_DE_PAGINA`).
 *
 * Location: src/construtor
 * Why: as páginas de fluxo (pagamento, presentes, perfil, parceiro) são as
 * páginas de sempre, com os textos da tradução da vitrine. Elas pegam texto
 * por `useTextosDaLoja().t(chave)`: com troca na língua atual vale a troca,
 * senão a tradução. Fora do layout Personalizado não existe troca, e o `t` é
 * o de sempre. Texto trocado vai para a tela como texto (o React escapa).
 */

type Textos = Record<string, TextoLocalizado>;

const TextosContext = createContext<Textos | null>(null);

export function TextosDaPaginaProvider({ textos, children }: { textos: Textos | undefined; children: ReactNode }) {
  return <TextosContext.Provider value={textos ?? null}>{children}</TextosContext.Provider>;
}

/** Troca `{{nome}}` pelo valor (o que não veio fica como está). */
export function interpolar(texto: string, valores?: Record<string, unknown>): string {
  if (!valores) return texto;
  return texto.replace(/\{\{\s*([a-zA-Z0-9_]+)\s*\}\}/g, (marca, nome: string) => {
    const v = valores[nome];
    return v === undefined || v === null ? marca : String(v);
  });
}

/** O texto trocado de uma chave na língua, ou `null` (vale a tradução da vitrine). */
export function textoTrocado(textos: Textos | null | undefined, chave: string, lingua: string): string | null {
  if (!textos || !Object.prototype.hasOwnProperty.call(textos, chave)) return null;
  const texto = textos[chave] ? textos[chave][lingua as Lingua] : undefined;
  return typeof texto === 'string' && texto.trim() ? texto : null;
}

export function linguaAtual(i18n: { resolvedLanguage?: string; language?: string }): string {
  return (i18n.resolvedLanguage || i18n.language || 'pt').split('-')[0];
}

/** `useTranslation` que respeita os textos trocados no construtor. */
export function useTextosDaLoja() {
  const traducao = useTranslation();
  const textos = useContext(TextosContext);
  const lingua = linguaAtual(traducao.i18n);
  const original = traducao.t;
  const t = useCallback(
    (chave: string, valores?: Record<string, unknown>): string => {
      const trocado = textoTrocado(textos, chave, lingua);
      if (trocado) return interpolar(trocado, valores);
      return String(valores ? original(chave, valores) : original(chave));
    },
    [textos, lingua, original],
  );
  return { i18n: traducao.i18n, t };
}
